import React, { useCallback, useState } from "react";

import {
  SubscriptionPlans,
  SubscriptionIntervals,
  SubscriptionStatus,
  SubscriptionProvider,
} from "@prisma/client";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

import Button from "@musicfy/components/Button";
import { FEATURE_ROUTES } from "@musicfy/components/Navigation/constants";
import { isFoundingDiscountApplied } from "@musicfy/contants/FoundingOffer";
import {
  getSubscriptionValue,
  type TPriceId,
  getSubscriptionPriceDetails,
  SubscriptionPlanInfo,
} from "@musicfy/contants/Subscriptions";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { usePaddleCheckout } from "@musicfy/libs/PaddleProvider/hooks";
import { useCheckoutProvider } from "@musicfy/libs/PaddleProvider/hooks/useCheckoutProvider";
import { usePaddleUpdatePayment } from "@musicfy/libs/PaddleProvider/hooks/usePaddleUpdatePayment";
import { usePaddleUpgrade } from "@musicfy/libs/PaddleProvider/hooks/usePaddleUpgrade";
import { useStripeCheckout } from "@musicfy/libs/PaddleProvider/hooks/useStripeCheckout";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { shouldStartFreshBillingCheckout } from "@musicfy/utils";
import { getCheckoutPromoCode } from "@musicfy/utils/checkoutPromo";
import {
  clearPendingCheckoutPriceId,
  setPendingCheckout,
} from "@musicfy/utils/pendingCheckout";

const checkoutButtonClassName =
  "text-20 !py-5 whitespace-nowrap !text-16 lg:!text-18";

const BaseUpdateSubscriptionButton: React.FC<{
  priceId: TPriceId;
  onClick: () => void;
  isLoading: boolean;
  disabled?: boolean;
  cta?: string;
  buttonClassName?: string;
  /** Treat like a new subscriber (no "Current" / upgrade labels). */
  forceFreshCheckoutLabels?: boolean;
  blockedLabel?: string;
  /** When set, blockedLabel is an actionable recovery CTA (not disabled). */
  blockedOnClick?: () => void;
}> = ({
  priceId,
  onClick,
  isLoading,
  disabled = false,
  cta,
  buttonClassName,
  forceFreshCheckoutLabels = false,
  blockedLabel,
  blockedOnClick,
}) => {
  const { subscription } = useSubscriptionContext();
  const buttonClasses = clsx(checkoutButtonClassName, buttonClassName);
  const isDisabled = isLoading || disabled;

  const { plan: pricePlan, interval: priceInterval } =
    getSubscriptionPriceDetails({ priceId });

  const currentPlan = subscription?.plan || SubscriptionPlans.free;
  const currentInterval = subscription?.interval || SubscriptionIntervals.month;

  const priceSubscriptionValue = getSubscriptionValue(pricePlan);
  const activeSubscriptionValue = getSubscriptionValue(currentPlan);

  const isCurrentPlan = pricePlan === currentPlan;
  const isCurrentInterval = priceInterval === currentInterval;

  if (blockedLabel) {
    return (
      <div className="w-full">
        <Button
          style={pricePlan === SubscriptionPlans.pro ? "primary" : "tertiary"}
          disabled={!blockedOnClick || isDisabled}
          fullWidth
          loading={isLoading}
          onClick={blockedOnClick}
          className={buttonClasses}
        >
          {blockedLabel}
        </Button>
      </div>
    );
  }

  // Non-billable paid users must be able to subscribe even to their "current" plan.
  if (isCurrentPlan && isCurrentInterval && !forceFreshCheckoutLabels) {
    return (
      <div className="w-full">
        <Button
          style={pricePlan === SubscriptionPlans.pro ? "primary" : "tertiary"}
          disabled
          fullWidth
          className={buttonClasses}
        >
          Current
        </Button>
      </div>
    );
  }

  if (isCurrentPlan && !forceFreshCheckoutLabels) {
    return (
      <div className="w-full">
        <Button
          loading={isLoading}
          disabled={isDisabled}
          fullWidth
          onClick={onClick}
          style={pricePlan === SubscriptionPlans.pro ? "primary" : "tertiary"}
          className={buttonClasses}
        >
          Switch to{" "}
          {priceInterval === SubscriptionIntervals.month ? "Monthly" : "Yearly"}
        </Button>
      </div>
    );
  }

  if (
    !forceFreshCheckoutLabels &&
    priceSubscriptionValue < activeSubscriptionValue
  ) {
    return (
      <div className="w-full">
        <Button
          loading={isLoading}
          disabled={isDisabled}
          fullWidth
          onClick={onClick}
          style={pricePlan === SubscriptionPlans.pro ? "primary" : "tertiary"}
          className={buttonClasses}
        >
          Downgrade
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Button
        loading={isLoading}
        disabled={isDisabled}
        fullWidth
        onClick={onClick}
        style={pricePlan === SubscriptionPlans.pro ? "primary" : "tertiary"}
        className={buttonClasses}
      >
        {cta ??
          (forceFreshCheckoutLabels
            ? `Subscribe to ${
                SubscriptionPlanInfo[pricePlan ?? SubscriptionPlans.free].label
              }`
            : `Choose ${
                SubscriptionPlanInfo[pricePlan ?? SubscriptionPlans.free].label
              }`)}
      </Button>
    </div>
  );
};

const StartCheckoutButton: React.FC<{
  priceId: TPriceId;
  discount?: { code?: string; id?: string };
  cta?: string;
  buttonClassName?: string;
  origin?: string;
}> = ({ priceId, discount, cta, buttonClassName, origin }) => {
  const { status } = useSession();
  const router = useRouter();
  const {
    setActivePaddleScreen,
    setOrigin,
    origin: paddleOrigin,
  } = usePaddleContext();
  const {
    subscription,
    paddleSubscription,
    isPaddleLoading,
    createStripePortalSession,
  } = useSubscriptionContext();
  const { checkoutProvider, ready: checkoutProviderReady } =
    useCheckoutProvider();

  const { startCheckout } = usePaddleCheckout();
  const { startUpgrade } = usePaddleUpgrade();
  const { startStripeCheckout, isStartingCheckout } = useStripeCheckout();
  const { startUpdatePayment } = usePaddleUpdatePayment();
  const { sendToastMessage } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);

  const paddleLoadFailed =
    !!subscription &&
    subscription.provider === "paddle" &&
    !subscription.subscriptionId.includes("temp") &&
    !isPaddleLoading &&
    !paddleSubscription;

  const isPaidSubscription =
    !!subscription && subscription.plan !== SubscriptionPlans.free;
  const isPaused =
    subscription?.status === SubscriptionStatus.paused && isPaidSubscription;
  const isPastDue =
    subscription?.status === SubscriptionStatus.past_due && isPaidSubscription;
  const isCanceled =
    subscription?.status === SubscriptionStatus.canceled && isPaidSubscription;

  const useFreshCheckout =
    !subscription ||
    subscription.plan === SubscriptionPlans.free ||
    isCanceled ||
    shouldStartFreshBillingCheckout(subscription, { paddleLoadFailed });

  // WELCOME50 only applies via Checkout.open — upgrade preview has no promo
  // field, so Starter / Pro-monthly founding CTAs must use checkout.
  const useFoundingCheckout = isFoundingDiscountApplied(discount);

  const updateSubscription = useCallback(async () => {
    if (isLoading || !checkoutProviderReady) return;

    const promoCode =
      discount?.code?.trim() || getCheckoutPromoCode() || undefined;

    if (status !== "authenticated") {
      if (status === "unauthenticated") {
        setPendingCheckout({
          priceId,
          discountCode: promoCode,
        });
        setActivePaddleScreen(null);
        void router.push({
          pathname: FEATURE_ROUTES.signup,
          query: { callbackPath: "/?show_pricing=1" },
        });
      }
      return;
    }

    // A manual authenticated selection supersedes any pre-signup choice.
    clearPendingCheckoutPriceId();
    if (origin) {
      setOrigin(origin);
    }
    setIsLoading(true);

    try {
      if (!(useFreshCheckout || useFoundingCheckout)) {
        startUpgrade(priceId);
        return;
      }

      // Founding WELCOME50 applies on Stripe Checkout too (Pro yearly).
      if (checkoutProvider === "stripe") {
        await startStripeCheckout({
          priceId,
          origin: origin || paddleOrigin,
          promoCode,
        });
        return;
      }

      startCheckout(
        priceId,
        discount ?? (promoCode ? { code: promoCode } : undefined)
      );
    } catch (err) {
      console.error("Checkout start failed", err);
      const message =
        err &&
        typeof err === "object" &&
        "message" in err &&
        typeof (err as { message: unknown }).message === "string"
          ? (err as { message: string }).message
          : "Unable to start checkout. Please try again.";
      sendToastMessage(message, { severity: "error" });
    } finally {
      setTimeout(() => setIsLoading(false), 2000);
    }
  }, [
    checkoutProvider,
    checkoutProviderReady,
    discount,
    isLoading,
    origin,
    paddleOrigin,
    priceId,
    router,
    setActivePaddleScreen,
    setOrigin,
    startCheckout,
    startStripeCheckout,
    startUpgrade,
    sendToastMessage,
    status,
    useFoundingCheckout,
    useFreshCheckout,
  ]);

  const recoverPastDuePayment = useCallback(() => {
    if (!subscription) {
      return;
    }
    if (subscription.provider === SubscriptionProvider.stripe) {
      createStripePortalSession();
      return;
    }
    void startUpdatePayment();
  }, [createStripePortalSession, startUpdatePayment, subscription]);

  return (
    <>
      <BaseUpdateSubscriptionButton
        priceId={priceId}
        onClick={() => {
          void updateSubscription();
        }}
        isLoading={isLoading || isStartingCheckout}
        disabled={!checkoutProviderReady}
        cta={cta}
        buttonClassName={buttonClassName}
        forceFreshCheckoutLabels={
          !!subscription &&
          subscription.plan !== SubscriptionPlans.free &&
          useFreshCheckout
        }
        blockedLabel={
          isPaused
            ? "Resume in Billing"
            : isPastDue
              ? "Update Payment Method"
              : undefined
        }
        blockedOnClick={isPastDue ? recoverPastDuePayment : undefined}
      />
    </>
  );
};

export default StartCheckoutButton;
